import { recordSelectedServices } from '../../face-identity/hooks/useRegisterServices';
import { fetchActiveVisit, mergePendingServicesToCheckin, startServiceCheckinQueue } from '../../salon-admin/utils/checkinQueueApi';
import {
  getFranchiseStylistId,
  readFranchiseFlowAuth,
} from '../../shared/franchiseFlowAuth';
import {
  getActiveCheckinSession,
  getSalonFaceUserId,
  persistSelectedServicesForRecommendations,
  readSessionSelectedServices,
  readVisitHandoff,
  setActiveCheckinSession,
} from './salonFaceSession';
import { resolveAndRestoreActiveVisit } from './visitHandoff';
import { coerceServiceStatus, SERVICE_IN_PROGRESS, SERVICE_PENDING } from './serviceLineUtils';

function normalizeServiceLabel(raw) {
  return String(raw || '').trim().toLowerCase();
}

function buildConfirmMatchKeys(lines) {
  const names = new Set();
  const ids = new Set();
  for (const line of lines || []) {
    const name = normalizeServiceLabel(line?.name);
    const id = normalizeServiceLabel(line?.id);
    if (name) names.add(name);
    if (id) ids.add(id);
  }
  return { names, ids };
}

function pendingLineMatchesConfirm(line, keys) {
  const name = normalizeServiceLabel(line?.name);
  const id = normalizeServiceLabel(line?.id);
  if (name && keys.names.has(name)) return true;
  if (id && keys.ids.has(id)) return true;
  return false;
}

function pickPendingLinesToStart(visit, confirmedList) {
  const keys = buildConfirmMatchKeys(confirmedList);
  const pending = (visit?.selected_services || []).filter(
    (line) => coerceServiceStatus(line?.service_status) === SERVICE_PENDING,
  );
  const matched = pending.filter((line) => pendingLineMatchesConfirm(line, keys));
  return matched.length ? matched : confirmedList;
}

function treatmentToLine(treatment, analysisSource) {
  if (!treatment) return null;
  const name = String(
    treatment.treatment ||
      treatment.service_name ||
      treatment.treatment_name ||
      treatment.service ||
      treatment.name ||
      '',
  ).trim();
  if (!name) return null;
  const line = {
    id: String(treatment.id || treatment.service_id || treatment.treatment_id || name),
    name,
    service_status: SERVICE_PENDING,
    analysis_source: analysisSource,
  };
  const price = String(treatment.price || treatment.price_text || '').trim();
  if (price) line.price = price;
  if (treatment.amount != null && treatment.amount !== '') {
    const amount = Number(treatment.amount);
    if (Number.isFinite(amount)) line.amount = amount;
  }
  const category = String(treatment.category || '').trim();
  if (category) line.category = category;
  return line;
}

export function treatmentCartToPendingServices(cartItems, analysisSource = 'skin_analysis') {
  const lines = [];
  Object.values(cartItems || {}).forEach((row) => {
    const qty = Math.max(0, Number(row?.quantity) || 0);
    if (!qty) return;
    const base = treatmentToLine(row?.treatment, analysisSource);
    if (!base) return;
    for (let i = 0; i < qty; i += 1) {
      lines.push({ ...base });
    }
  });
  return lines;
}

export function hairTreatmentsToPendingServices(treatments, selectedKeys, analysisSource = 'hair_analysis') {
  const list = Array.isArray(treatments) ? treatments : [];
  const keys = selectedKeys instanceof Set ? selectedKeys : new Set(selectedKeys || []);
  const lines = [];
  list.forEach((t, idx) => {
    const key = String(t?.id || t?.name || idx);
    if (keys.size && !keys.has(key)) return;
    const line = treatmentToLine(t, analysisSource);
    if (line) lines.push(line);
  });
  return lines;
}

export async function resolveOpenCheckinId() {
  const active = getActiveCheckinSession();
  if (active?.id && active.status !== 'completed' && active.status !== 'cancelled') {
    return active.id;
  }
  const handoff = readVisitHandoff();
  if (handoff?.checkinId) return handoff.checkinId;
  try {
    const restored = await resolveAndRestoreActiveVisit();
    return restored?.id || null;
  } catch {
    return null;
  }
}

let syncTimer = null;
let lastSyncKey = '';

export function readLocalPendingServiceLines() {
  const local = readSessionSelectedServices();
  return local.filter((line) => coerceServiceStatus(line?.service_status) === SERVICE_PENDING);
}

export function scheduleSyncAnalysisPendingServices(lines, analysisSource) {
  const list = Array.isArray(lines) ? lines.filter(Boolean) : [];
  const checkinHint = getActiveCheckinSession()?.id || readVisitHandoff()?.checkinId || '';
  const key = JSON.stringify({
    checkinHint,
    analysisSource,
    list: list.map((l) => `${l.id || ''}:${l.name}`),
  });
  if (key === lastSyncKey) return;
  lastSyncKey = key;

  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    syncAnalysisPendingServices(list, analysisSource).catch(() => {});
  }, 400);
}

export async function syncAnalysisPendingServices(lines, analysisSource = 'analysis') {
  const list = Array.isArray(lines) ? lines.filter((l) => l?.name) : [];
  if (!list.length) return null;
  persistSelectedServicesForRecommendations(list);
  const checkinId = await resolveOpenCheckinId();
  if (!checkinId) return null;
  const updated = await mergePendingServicesToCheckin(checkinId, list, { analysisSource });
  if (updated?.id) {
    setActiveCheckinSession({
      id: updated.id,
      code: updated.customer_code,
      status: updated.status,
      faceUserId: updated.face_user_id,
    });
  }
  try {
    window.dispatchEvent(new CustomEvent('checkin-queue-updated'));
  } catch {
    /* ignore */
  }
  return updated;
}

function resolveAssignedStaff(visit) {
  const fromVisit = Array.isArray(visit?.assigned_staff)
    ? visit.assigned_staff
        .map((p) => ({
          id: String(p?.id || '').trim(),
          name: String(p?.name || '').trim(),
        }))
        .filter((p) => p.id || p.name)
    : [];
  if (fromVisit.length) return fromVisit;

  const handoff = readVisitHandoff();
  if (handoff?.stylistId) {
    return [
      {
        id: String(handoff.stylistId),
        name: String(handoff.stylistName || '').trim(),
      },
    ];
  }

  const auth = readFranchiseFlowAuth();
  const id = getFranchiseStylistId();
  if (id) {
    return [
      {
        id: String(id),
        name: String(auth?.full_name || auth?.username || '').trim(),
      },
    ];
  }

  if (visit?.stylist_id || visit?.stylist_name) {
    return [
      {
        id: String(visit.stylist_id || '').trim(),
        name: String(visit.stylist_name || '').trim(),
      },
    ].filter((p) => p.id || p.name);
  }
  return [];
}

/**
 * After skin/hair treatment confirm: ensure lines are on the visit and mark them
 * in_progress (started) with the assigned stylist.
 */
export async function confirmAnalysisServicesAndStart(lines, analysisSource = 'analysis') {
  const list = Array.isArray(lines) ? lines.filter((l) => l?.name) : [];
  if (!list.length) {
    throw new Error('Select at least one service to confirm.');
  }

  persistSelectedServicesForRecommendations(list);
  const checkinId = await resolveOpenCheckinId();
  if (!checkinId) {
    throw new Error('No active visit check-in. Check in at the front desk, then confirm again.');
  }

  let visit = null;
  try {
    visit = await fetchActiveVisit({ checkinId });
  } catch {
    visit = null;
  }
  const staff = resolveAssignedStaff(visit);
  const toStart = pickPendingLinesToStart(visit, list);
  const withStaff = toStart.map((line) => ({
    ...line,
    service_status: SERVICE_IN_PROGRESS,
    ...(staff.length ? { staff } : {}),
  }));

  let updated = await mergePendingServicesToCheckin(checkinId, list, {
    analysisSource,
    startServices: true,
    assignedStaff: staff.length ? staff : undefined,
    startSelectedServices: withStaff,
  });

  const hasStarted = (updated?.selected_services || []).some(
    (line) => coerceServiceStatus(line?.service_status) === SERVICE_IN_PROGRESS,
  );
  if (!hasStarted) {
    updated = await startServiceCheckinQueue(checkinId, {
      assignedStaff: staff.length ? staff : undefined,
      selectedServices: withStaff,
    });
  }

  if (!updated?.id) {
    throw new Error('Could not start services. Try again from the service desk.');
  }

  setActiveCheckinSession({
    id: updated.id,
    code: updated.customer_code,
    status: updated.status || 'in_service',
    faceUserId: updated.face_user_id,
  });
  persistSelectedServicesForRecommendations(
    (updated.selected_services || []).filter(
      (line) => coerceServiceStatus(line?.service_status) === SERVICE_IN_PROGRESS,
    ).length
      ? updated.selected_services
      : withStaff,
  );
  lastSyncKey = '';

  const faceUserId =
    updated?.face_user_id ||
    visit?.face_user_id ||
    getActiveCheckinSession()?.faceUserId ||
    getSalonFaceUserId() ||
    '';
  if (faceUserId) {
    try {
      await recordSelectedServices({
        faceUserId,
        selectedServices: withStaff,
        source: String(analysisSource || 'analysis').slice(0, 40) || 'analysis',
        visitId: updated?.id || checkinId,
      });
    } catch {
      /* non-blocking — complete_checkin also persists history */
    }
  }

  try {
    window.dispatchEvent(new CustomEvent('checkin-queue-updated'));
  } catch {
    /* ignore */
  }
  return updated;
}

/** Push analysis product cart selections onto the open check-in for front desk. */
export async function syncAnalysisProductCartToCheckin(productCartItems, source = 'analysis') {
  const checkinId = await resolveOpenCheckinId();
  if (!checkinId) return null;
  const items = Object.values(productCartItems || {})
    .filter((row) => row && Number(row.quantity) > 0 && row.product)
    .map((row) => ({
      product: row.product,
      quantity: Math.max(1, Number(row.quantity) || 1),
    }));
  try {
    const { saveCheckinProductCart } = await import('../../salon-admin/utils/checkinQueueApi');
    const data = await saveCheckinProductCart(checkinId, items, { source });
    try {
      window.dispatchEvent(new CustomEvent('checkin-queue-updated'));
    } catch {
      /* ignore */
    }
    return data;
  } catch {
    return null;
  }
}

/**
 * After skin/hair product recommendations are generated, stamp the open check-in
 * so lobby / frontdesk can Preview products even when no service was taken.
 */
export async function syncAnalysisRecommendedProductsReady(
  source = 'analysis',
  { salonReference = '', hairInsightSessionId = '', productCount = null } = {},
) {
  const checkinId = await resolveOpenCheckinId();
  if (!checkinId) return null;
  try {
    const { markCheckinAnalysisProductsReady } = await import(
      '../../salon-admin/utils/checkinQueueApi'
    );
    const data = await markCheckinAnalysisProductsReady(checkinId, {
      source,
      salonReference,
      hairInsightSessionId,
      productCount,
    });
    try {
      window.dispatchEvent(new CustomEvent('checkin-queue-updated'));
    } catch {
      /* ignore */
    }
    return data;
  } catch {
    return null;
  }
}

export function pendingAnalysisLines(selectedServices) {
  const list = Array.isArray(selectedServices) ? selectedServices : [];
  return list.filter((line) => coerceServiceStatus(line?.service_status) === SERVICE_PENDING);
}
